import React, { useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  CardActionArea, 
  Box, 
  Chip, 
  styled,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FolderIcon from '@mui/icons-material/Folder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Project } from '../../interfaces/project';

// Estilos
const ProjectCardContainer = styled(Card)(({ theme }) => ({
  backgroundColor: '#1e1e2d',
  color: 'white',
  borderRadius: '8px',
  transition: 'transform 0.2s, box-shadow 0.2s',
  height: '100%',
  minHeight: '180px', // Altura mínima para manter tamanho consistente
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative', // Garante que elementos absolutos ficam dentro do card
  overflow: 'visible', // Permite que o botão de exclusão seja visível
  margin: '0', // Elimina qualquer margem automática
  padding: '0', // Elimina qualquer padding automático
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.4)',
  },
  '&:hover .delete-button': {
    opacity: 1,
  },
}));

const CardActionAreaStyled = styled(CardActionArea)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  height: '100%',
}));

const ProjectIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '8px',
  backgroundColor: '#2196f3',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  marginBottom: '12px',
}));

const ProjectCardContent = styled(CardContent)(({ theme }) => ({
  padding: '20px', // Aumentado o padding
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
}));

const ProjectTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: '12px', // Aumentado para dar mais espaço
  fontSize: '1.1rem', // Tamanho de fonte ligeiramente maior
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const ProjectDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  marginBottom: '16px', // Ligeiramente reduzido para telas menores
  flexGrow: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2, // Reduzido para 2 linhas para caber melhor em telas menores
  WebkitBoxOrient: 'vertical',
  lineHeight: '1.4', // Melhor espaçamento entre linhas
  fontSize: '0.85rem', // Tamanho de fonte ligeiramente menor
  '@media (max-width: 768px)': {
    WebkitLineClamp: 2,
    marginBottom: '12px',
  },
}));

const ProjectMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 'auto',
}));

const ProjectDate = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: 'rgba(255, 255, 255, 0.6)',
  fontSize: '0.75rem',
}));

const ProjectStatusChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
  fontSize: '0.7rem',
  height: '24px',
}));

const MembersAvatars = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginTop: '12px',
  alignItems: 'center',
  '@media (max-width: 768px)': {
    marginTop: '8px',
  }
}));

const MemberAvatar = styled(Avatar)(({ theme }) => ({
  width: 28, // Ligeiramente maior
  height: 28, // Ligeiramente maior
  fontSize: '0.8rem', // Adaptado para o novo tamanho
  marginRight: -12, // Ajustado para o novo tamanho
  border: '2px solid #1e1e2d',
  backgroundColor: '#2196f3',
}));

const MoreButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '12px',
  right: '12px',
  color: 'white',
  padding: '8px',
  backgroundColor: 'transparent',
  zIndex: 20,
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out, background-color 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

interface ProjectCardProps {
  project: Project;
  isAdmin?: boolean;
  onDeleteClick?: (projectId: string) => void;
  onEditClick?: (project: Project) => void;
}

// Função auxiliar para garantir um valor booleano
function ensureBoolean(value: any): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, isAdmin = false, onDeleteClick, onEditClick }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  
  // Função para lidar com possíveis inconsistências na estrutura do projeto
  const getProjectDate = () => {
    // Tentar obter uma data válida do projeto, verificando diferentes campos possíveis
    const date = project.lastUpdatedAt || project.updatedAt || project.createdAt;
    
    if (!date) return 'Data desconhecida';
    
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : new Date(date);
      return formatDistanceToNow(parsedDate, { addSuffix: true, locale: ptBR });
    } catch (error) {
      console.error('Erro ao analisar data do projeto:', error);
      return 'Data inválida';
    }
  };
  
  const getProjectTitle = () => {
    // Priorizar title sobre name para compatibilidade
    return project.title || project.name || 'Projeto sem título';
  };
  
  const getProjectDescription = () => {
    return project.description || 'Sem descrição';
  };
  
  const getProjectId = () => {
    // Garantir que temos uma string de ID válida
    if (!project.id) {
      console.error('Projeto sem ID válido:', project);
      return '';
    }
    return project.id;
  };

  const handleClick = () => {
    const id = getProjectId();
    if (id) {
      navigate(`/project/${id}`);
    } else {
      console.error('Não foi possível navegar: ID de projeto inválido');
    }
  };
  
  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Evita que o clique propague para o card
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = (e?: React.MouseEvent | {}, reason?: string) => {
    // Se for um evento de mouse, impedir a propagação
    if (e && 'stopPropagation' in e) {
      e.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Evita que o clique propague para o card
    handleCloseMenu();
    if (project.id && onDeleteClick) {
      onDeleteClick(project.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // Evita que o clique propague para o card
    handleCloseMenu();
    if (project.id && onEditClick) {
      onEditClick(project);
    }
  };


  // Usar a função auxiliar para garantir um valor booleano
  const showDeleteButton = ensureBoolean(isAdmin);

  return (
    <ProjectCardContainer>
      {/* Menu de ações - só exibido para administradores */}
      {showDeleteButton && (
        <>
          <MoreButton 
            onClick={handleOpenMenu} 
            size="small"
            data-testid="project-actions-button"
            className="delete-button"
            title="Opções"
          >
            <MoreVertIcon fontSize="small" />
          </MoreButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            onClick={(e) => e.stopPropagation()}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                backgroundColor: '#1e1e2d',
                color: 'white',
                minWidth: '180px',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)',
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }
            }}
          >
            <MenuItem onClick={handleEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" sx={{ color: '#2196f3' }} />
              </ListItemIcon>
              <ListItemText>Editar projeto</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: '#ff5757' }} />
              </ListItemIcon>
              <ListItemText>Excluir projeto</ListItemText>
            </MenuItem>
          </Menu>
        </>
      )}
      <CardActionAreaStyled onClick={handleClick}>
        <ProjectCardContent>
          <ProjectIcon>
            <FolderIcon />
          </ProjectIcon>
          
          <ProjectTitle variant="h6">
            {getProjectTitle()}
          </ProjectTitle>
          
          <ProjectDescription variant="body2">
            {getProjectDescription()}
          </ProjectDescription>
          
          {/* <ProjectMeta>
            <ProjectDate>
              <AccessTimeIcon sx={{ fontSize: 14 }} />
              {getProjectDate()}
            </ProjectDate>
            
            <ProjectStatusChip 
              label="Ativo" 
              size="small"
            />
          </ProjectMeta>
          
          <MembersAvatars>
            {project.members?.slice(0, 3).map((member, index) => (
              <MemberAvatar key={index}>
                {member.firstName?.charAt(0) || member.name?.charAt(0) || '?'}
              </MemberAvatar>
            ))}
            {project.members && project.members.length > 3 && (
              <MemberAvatar>
                +{project.members.length - 3}
              </MemberAvatar>
            )}
            {(!project.members || project.members.length === 0) && (
              <MemberAvatar>?</MemberAvatar>
            )}
          </MembersAvatars> */}
        </ProjectCardContent>
      </CardActionAreaStyled>
    </ProjectCardContainer>
  );
};

export default ProjectCard;